/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../core/components';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DashboardDataModel, OrganisationModel } from '../../shared/models';
import { DashboardService } from '../../shared/services/component';
import { DropdownModel, ErrorResponseModel, UserSessionModel } from '../../../../core/models';
import { LogService, NotifyService, UserService, UtilsService } from '../../../../core/services';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent extends BaseComponent implements OnInit, OnDestroy {
    /**
     * Selected Namespace
     */
    public selectedNamespace: DropdownModel;

    /**
     * Store the organisation details for the view.
     */
    public organisationDetails: OrganisationModel;

    /**
     * Stored user data.
     */
    public storedUserData: UserSessionModel;

    /**
     * Store the current user first name.
     */
    public currentUserFirstName: string;

    constructor(
        private dashboardService: DashboardService,
        private logService: LogService,
        private notifyService: NotifyService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();
        this.notifyService.muteLocalNotifications();
        this.storedUserData = this.utilsService.duplicateObject(this.userService.getUser());
    }

    ngOnInit(): void {
        const data: DashboardDataModel = this.dashboardService.allData;
        this.setUpClassVariables(data);
        if ((Date.now() - data['timeStamp']) > 10000) { // Get updated data if the time is too great.
            this.dashboardService.getServiceData(true)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: DashboardDataModel) => {
                        this.setUpClassVariables(data);
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('DashboardComponent:ngOnInit', 'Error getting dashboard data.', error);
                    }
                });
        }
    }

    ngOnDestroy(): void {
        this.notifyService.unmuteLocalNotifications();
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    /**
     * Set up class variables.
     * @param data
     * @private
     */
    private setUpClassVariables(data: DashboardDataModel): void {
        if (data) {
            const currentUser: any = data.users.find((item: any): boolean => item.id?.toString() === this.storedUserData.id?.toString());
            this.currentUserFirstName = currentUser?.firstName;
            this.organisationDetails = data?.organisation;
        }
    }
}
